package navigation.home.edititineraryservice

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.*
import androidx.compose.runtime.getValue
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.core.screen.ScreenKey
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.essntl.features.address.domain.model.AddressModel
import com.essntl.features.contact.domain.model.ContactModel
import com.essntl.features.contact.ui.ContactEvent
import com.essntl.features.contact.ui.components.ContactDialog
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryEvent
import com.essntl.features.itinerary.ui.edit_itinerary.MessageSendType
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceEvent
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceNavigation
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceScreen
import com.essntl.features.service.ui.components.ServiceHeaderSection
import com.essntl.features.supplier.ui.ChangeSupplierPopup
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.button.secondary.EsLargeSecondaryButton
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.mohamedrejeb.calf.core.LocalPlatformContext
import core.config.FeatureConfig
import navigation.home.address.AddressScreenModel
import navigation.home.contact.ContactScreenModel
import navigation.home.edititinerary.EditItineraryScreenModel
import navigation.home.send_message.SendMessageScreenNav
import org.koin.core.qualifier.named

class EditItineraryServiceScreenNav(
    private val itineraryServiceId: String,
    private val itineraryId: String?,
    private val onNavigation: (EditItineraryServiceNavigation) -> Unit = {},
    private val onSuccessMessage: () -> Unit = {},
) : Screen {

    override val key: ScreenKey = "EditItineraryServiceScreenNav/$itineraryId/$itineraryServiceId"

    @Composable
    override fun Content() {
        val editItineraryServiceScreenModel = koinScreenModel<EditItineraryServiceScreenModel>()
        val editItineraryServiceState by editItineraryServiceScreenModel.state.collectAsState()

        val editItineraryScreenModel = koinScreenModel<EditItineraryScreenModel>()
        val editItineraryState by editItineraryScreenModel.state.collectAsState()

        val contactScreenModel = koinScreenModel<ContactScreenModel>(named("itineraryService"))
        val contactState by contactScreenModel.state.collectAsState()

        val addressScreenModel = koinScreenModel<AddressScreenModel>()
        val addressState by addressScreenModel.state.collectAsState()

        val supplierContactScreenModel = koinScreenModel<ContactScreenModel>(named("supplier"))
        val supplierContactState by supplierContactScreenModel.state.collectAsState()

        var isChangeSupplierPopupOpen by rememberSaveable { mutableStateOf(false) }

        var openAddEditContactDialog by rememberSaveable { mutableStateOf(false) }
        var contactToEdit by remember { mutableStateOf<ContactModel?>(null) }

        val context = LocalPlatformContext.current

        LaunchedEffect(itineraryServiceId, itineraryId) {
            itineraryId?.let { itineraryId ->
                editItineraryServiceScreenModel.onEvent(
                    EditItineraryServiceEvent.GetItineraryService(
                        itineraryServiceId = itineraryServiceId,
                        itineraryId = itineraryId,
                    ),
                )
            }
        }

        LaunchedEffect(editItineraryServiceState.getItineraryService) {
            val service = editItineraryServiceState.getItineraryService.data?.service
            if (service != null)
                editItineraryServiceScreenModel.onEvent(
                    EditItineraryServiceEvent.GetSuppliers(service = service),
                )
        }

        LaunchedEffect(editItineraryState.getAllItineraryService) {
            editItineraryScreenModel.onEvent(EditItineraryEvent.GetItineraryServicesMessages)
        }

        LaunchedEffect(editItineraryServiceState.getSupplier.isSuccess) {
            if (editItineraryServiceState.getSupplier.isSuccess) {
                val contact = editItineraryServiceState.getSupplier.data?.contact

                val itineraryService = editItineraryServiceState.itineraryService.value

                editItineraryServiceScreenModel.onEvent(
                    EditItineraryServiceEvent.UpdateItineraryServiceFields(
                        itineraryService = itineraryService.copy(
                            supplierContact = null,
                        ),
                    ),
                )

                editItineraryServiceScreenModel.onEvent(EditItineraryServiceEvent.UpdateItineraryService(context))

                val organizationId = contact?.organization?.id
                organizationId?.let { id ->
                    contactScreenModel.onEvent(
                        ContactEvent.GetOrganizationContacts(
                            organizationId = id,
                        ),
                    )
                }

                editItineraryServiceScreenModel.onEvent(EditItineraryServiceEvent.ClearSupplier)
            }
        }

        LaunchedEffect(contactState.editContact.isSuccess) {
            if (contactState.editContact.isSuccess) {
                contactState.editContact.data?.let { contact ->
                    editItineraryServiceScreenModel.onEvent(
                        EditItineraryServiceEvent.UpdateItineraryServiceFields(
                            itineraryService = editItineraryServiceState.itineraryService.value.copy(
                                supplierContact = contact,
                            ),
                        ),
                    )

                    val organizationId = contact.organization?.id
                    organizationId?.let { id ->
                        contactScreenModel.onEvent(
                            ContactEvent.GetOrganizationContacts(
                                organizationId = id,
                            ),
                        )
                    }
                }
                openAddEditContactDialog = false
                contactScreenModel.onEvent(ContactEvent.ClearSuccessState)
            }
        }

        LaunchedEffect(editItineraryServiceState.setPlace.isSuccess) {
            if (editItineraryServiceState.setPlace.isSuccess) {
                editItineraryServiceState.setPlace.data?.let { addressId ->
                    editItineraryServiceScreenModel.onEvent(
                        EditItineraryServiceEvent.UpdateItineraryServiceFields(
                            itineraryService = editItineraryServiceState.itineraryService.value.copy(
                                address = AddressModel.empty.copy(
                                    id = addressId,
                                ),
                            ),
                        ),
                    )

                    editItineraryServiceScreenModel.onEvent(EditItineraryServiceEvent.ClearPlace)

                    editItineraryServiceScreenModel.onEvent(EditItineraryServiceEvent.UpdateItineraryService(context))
                }
            }
        }

        val service = remember(editItineraryServiceState.getItineraryService.data) {
            editItineraryServiceState.getItineraryService.data?.service
        }

        val category =
            remember(
                service,
                editItineraryState.getAllCategory.data,
            ) {
                editItineraryState.getAllCategory.data?.find { it.id == service?.category }
            }

        val navigator = LocalNavigator.currentOrThrow

        Box(
            modifier = Modifier
                .fillMaxSize(),
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight(0.925f),
            ) {
                ServiceHeaderSection(
                    title = service?.title.orEmpty(),
                    referenceCode = service?.referenceCode.orEmpty(),
                    onArrowClick = {},
                    showArrow = false,
                    modifier = Modifier
                        .padding(horizontal = 2.5.spacing)
                        .padding(bottom = 1.spacing),
                )

                EditItineraryServiceScreen(
                    state = editItineraryServiceState,
                    onEvent = editItineraryServiceScreenModel::onEvent,
                    validation = editItineraryServiceScreenModel.validation,
                    category = category,
                    contactState = contactState,
                    contactEvent = contactScreenModel::onEvent,
                    onNavigation = {
                        if (it is EditItineraryServiceNavigation.SendMessage)
                            itineraryId?.let { id ->
                                navigator.push(
                                    SendMessageScreenNav(
                                        itineraryId = id,
                                        itineraryServiceId = itineraryServiceId,
                                        type = it.type,
                                        onSuccessMessage = onSuccessMessage,
                                    ),
                                )
                            }
                        else if (it is EditItineraryServiceNavigation.ChangeSupplier)
                            isChangeSupplierPopupOpen = true
                        else if (it is EditItineraryServiceNavigation.AddContact)
                            openAddEditContactDialog = true
                        else if (it is EditItineraryServiceNavigation.EditContact) {
                            contactToEdit = it.contact
                            openAddEditContactDialog = true
                        } else
                            onNavigation(it)
                    },
                )
            }

            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center,
                modifier =
                    Modifier
                        .align(Alignment.BottomCenter)
                        .background(
                            color = MaterialTheme.esColorScheme.bgSecondary,
                        )
                        .fillMaxWidth()
                        .padding(2.5.spacing),
            ) {
                EsLargePrimaryButton(
                    onClick = {
                        if (editItineraryServiceScreenModel.validation.editItineraryServiceForm.validate())
                            if (editItineraryServiceState.selectedPlace != null) {
                                editItineraryServiceScreenModel.onEvent(
                                    EditItineraryServiceEvent.SetPlace,
                                )
                            } else {
                                editItineraryServiceScreenModel.onEvent(
                                    EditItineraryServiceEvent.UpdateItineraryService(context),
                                )
                            }
                    },
                    loading = editItineraryServiceState.updateItineraryService.isLoading ||
                        editItineraryServiceState.setPlace.isLoading,
                    text = "Save",
                    enabled =
                        (editItineraryServiceState.isEditEnabled ||
                            editItineraryServiceState.selectedPlace != null) &&
                            editItineraryServiceScreenModel.validation.editItineraryServiceForm.isValid,
                    modifier = Modifier.weight(1f),
                )

                Spacer(modifier = Modifier.width(width = 1.5.spacing))

                EsLargeSecondaryButton(
                    onClick = {
                        itineraryId?.let { id ->
                            navigator.push(
                                SendMessageScreenNav(
                                    itineraryId = id,
                                    itineraryServiceId = itineraryServiceId,
                                    type = MessageSendType.TRAVEL_AGENT,
                                    onSuccessMessage = onSuccessMessage,
                                ),
                            )
                        }
                    },
                    text = "Contact travel agent",
                    modifier =
                        Modifier
                            .weight(1f),
                )

                if (FeatureConfig.hasWhatsapp) {
                    Spacer(modifier = Modifier.width(width = 1.5.spacing))

                    EsLargeSecondaryButton(
                        onClick = {
                            itineraryId?.let { id ->
                                navigator.push(
                                    SendMessageScreenNav(
                                        itineraryId = id,
                                        itineraryServiceId = itineraryServiceId,
                                        type = MessageSendType.CLIENT,
                                        onSuccessMessage = onSuccessMessage,
                                    ),
                                )
                            }
                        },
                        text = "Contact traveller",
                        enabled = editItineraryServiceState.itineraryService.value.status == ItineraryServiceModel.Status.Confirmed,
                        modifier = Modifier.weight(1f),
                    )
                }
            }

            ChangeSupplierPopup(
                state = supplierContactState,
                onEvent = supplierContactScreenModel::onEvent,
                title = "Set supplier",
                buttonText = "Save",
                favoriteSuppliers = editItineraryServiceState.getAllSuppliers.data
                    .orEmpty()
                    .map { it.supplier },
                isOpen = isChangeSupplierPopupOpen,
                onSelectSupplier = {
                    val itineraryService = editItineraryServiceState.itineraryService.value

                    if (it.id != itineraryService.supplier?.id)
                        editItineraryServiceScreenModel.onEvent(
                            EditItineraryServiceEvent.UpdateItineraryServiceSupplier(
                                itineraryService = itineraryService,
                                supplier = it,
                            ),
                        )
                },
                onDismissRequest = {
                    isChangeSupplierPopupOpen = false
                },
            )

            ContactDialog(
                state = contactState,
                onEvent = contactScreenModel::onEvent,
                validation = contactScreenModel.validation,
                contact = contactToEdit,
                addressState = addressState,
                onAddressEvent = addressScreenModel::onEvent,
                addressValidation = addressScreenModel.validation,
                organizationId = editItineraryServiceState.itineraryService.value.supplier?.contact?.organization?.id,
                isOpen = openAddEditContactDialog,
                onDismissRequest = {
                    openAddEditContactDialog = false
                    contactToEdit = null
                },
            )
        }
    }
}